import { prefersReducedMotion } from '@theme/utilities';

/**
 * Owns the hover/focus state of a MushiButton, ported from the Webflow prototype's IX2 events
 * e-863 / e-864 (action lists a-35 / a-36) - see WOM-NOTES for the decoded interaction.
 *
 * Every visual is CSS keyed off the data-active attribute; this only flips that attribute and drives
 * the Lottie star layers, which mount paused via data-autoplay="false".
 */
class WomButtonFx extends HTMLElement {
  #active = false;

  connectedCallback() {
    if (prefersReducedMotion()) return;

    // Listeners live on the element itself, so a Section Rendering API re-render re-runs this and rewires them.
    this.addEventListener('pointerenter', this.#activate);
    this.addEventListener('pointerleave', this.#deactivate);
    this.addEventListener('focusin', this.#activate);
    this.addEventListener('focusout', this.#deactivate);
  }

  disconnectedCallback() {
    this.removeEventListener('pointerenter', this.#activate);
    this.removeEventListener('pointerleave', this.#deactivate);
    this.removeEventListener('focusin', this.#activate);
    this.removeEventListener('focusout', this.#deactivate);
  }

  // A sold-out or linkless button keeps the effect suppressed, matching the CSS in wom-button-fx.
  get #disabled() {
    return Boolean(this.querySelector('[disabled], [aria-disabled="true"]'));
  }

  #activate = () => {
    if (this.#active || this.#disabled) return;
    this.#active = true;
    this.toggleAttribute('data-active', true);
    this.#syncLayers();
  };

  #deactivate = () => {
    if (!this.#active) return;
    this.#active = false;
    this.toggleAttribute('data-active', false);
    this.#syncLayers();
  };

  /**
   * Drives every star layer to the current state, waiting for the element to upgrade first - the
   * layers can be parsed before wom-lottie.js has evaluated, and un-upgraded elements have no
   * play/pause yet. State is re-read after the await so a quick hover in-and-out settles correctly.
   */
  async #syncLayers() {
    const layers = this.querySelectorAll('wom-lottie');
    if (!layers.length) return;

    await customElements.whenDefined('wom-lottie');
    if (!this.isConnected) return;

    for (const layer of /** @type {NodeListOf<any>} */ (layers)) {
      this.#active ? layer.goToAndPlay(0) : layer.pause();
    }
  }
}

if (!customElements.get('wom-button-fx')) {
  customElements.define('wom-button-fx', WomButtonFx);
}
